/**
 * @puffin-core/auth-client — core session operations.
 *
 * Raw projections (no token storage — caller stores; for pos standalone +
 * no-AuthProvider surfaces) and *ForContext projections (store tokens + return
 * the `{user, locations}` shape `createAuthContext` consumes).
 *
 * AUTHENTICATOR-CHALLENGE-FANOUT (2026-08-29) adds `completeAuthenticatorChallenge` to that second
 * group: the one place an already-minted push-2FA session is written to the token store, so the
 * nine web apps delegate to the existing persistence path instead of nine hand-rolled
 * `authStorage.setAuth(...)` call sites.
 *
 * @module @puffin-core/auth-client/operations/core
 */

import { extractData, authError, networkError, throwIfUnreachable } from '../internal.js';
import {
  readEnrollmentRequired,
  buildMfaEnrollmentOperations,
  buildPasskeyEnrollmentOperations,
  buildPinEnrollmentOperations,
} from '../mfaEnrollment.js';

const NETWORK_ERROR = 'Network error. Please check your connection and try again.';

function mfaChallenge(rd) {
  return {
    requiresMfa: true,
    mfaToken: rd.mfaToken,
    // The tenant login always answers both (auth-routes `tenantAuth`); a default here invented a method the person
    // may not hold (Q-20, 2026-10-19).
    mfaMethods: rd.mfaMethods,
    preferredMethod: rd.preferredMethod,
    maskedEmail: rd.maskedEmail || null,
    maskedPhone: rd.maskedPhone || null,
  };
}

/**
 * AUTHENTICATOR-CHALLENGE-FANOUT (2026-08-29) — the push-2FA projection.
 *
 * 🔴 This exists because `mfaChallenge()` above is a REBUILD, not a pass-through: it names six
 * fields and drops everything else. `packages/auth-routes/lib/tenantAuth.js` answers a user who
 * holds BOTH factors with `{requiresMfa, mfaToken, …, requiresAuthenticator, challengeId,
 * numberMatch, expiresAt}` — so routing that response through `mfaChallenge()` alone deleted the
 * challenge and the login page could never render the screen the user's phone was already showing.
 *
 * The two server shapes (read from `tenantAuth.js`, not assumed):
 *   1. both factors → the merged object above; `mfaToken` present, so "Use code instead" is real.
 *   2. push only    → `{requiresAuthenticator, challengeId, numberMatch, expiresAt}`; NO `mfaToken`,
 *      because this app either has no TOTP for the user or mounts no MFA at all.
 *
 * Shape 2 is why the MFA half is spread CONDITIONALLY rather than always: emitting
 * `requiresMfa: true` with an undefined `mfaToken` would offer a code-entry screen that cannot
 * verify anything.
 *
 * @param {Object} rd - the login response's unwrapped `data`
 * @returns {Object} the challenge, merged with the MFA challenge when the server sent one
 */
function authenticatorChallenge(rd) {
  const challenge = {
    requiresAuthenticator: true,
    challengeId: rd.challengeId,
    numberMatch: rd.numberMatch,
    expiresAt: rd.expiresAt || null,
  };
  return rd.requiresMfa ? { ...challenge, ...mfaChallenge(rd) } : challenge;
}

/**
 * @param {Object} ctx
 * @returns {Object} core methods
 */
export function buildCoreOperations(ctx) {
  const { request, endpoints, tokenStore, refreshEndpoint, deviceTokenField } = ctx;

  // The refresh token is NOT persisted: the server holds it in an httpOnly cookie and the client
  // keeps only the CSRF token bound to it (`@puffin-core/auth-token-storage`).
  function persist(rd) {
    tokenStore.setAuth({
      accessToken: rd.accessToken,
      csrfToken: rd.csrfToken,
      user: rd.user,
      locations: rd.locations,
    });
  }

  // The tenant ENROLMENT leg (owner ruling Q-ARG-1): begin with the password, complete with the first code; the
  // session the server mints on completion is persisted through the SAME `persist()` a password login uses.
  const enrollment = buildMfaEnrollmentOperations({
    request,
    beginPath: endpoints.mfaEnrollmentBegin,
    completePath: endpoints.mfaEnrollmentComplete,
    persist,
    project: (rd) => ({ user: rd.user, locations: rd.locations }),
    networkMessage: NETWORK_ERROR,
  });

  // SECREQ S6b: the passkey enrolment a sign-in held for a passkey opens, over the passkey router's own registration
  // routes; the session it mints is persisted through the SAME `persist()`.
  const passkeyEnrollment = buildPasskeyEnrollmentOperations({
    request,
    optionsPath: endpoints.passkeyRegisterOptions,
    registerPath: endpoints.passkeyRegister,
    persist,
    project: (rd) => ({ user: rd.user, locations: rd.locations }),
    networkMessage: NETWORK_ERROR,
  });

  // SECREQ S6c (c): the PIN a sign-in held for a PIN is set with, over the device-PIN register route; the session it
  // mints is persisted through the SAME `persist()`. Only pos's seat holds for a PIN, so only pos's context serves it.
  const pinEnrollment = buildPinEnrollmentOperations({
    request,
    registerPath: endpoints.registerDevicePin,
    persist,
    project: (rd) => ({ user: rd.user, locations: rd.locations }),
    networkMessage: NETWORK_ERROR,
  });

  // --- Raw login (pos standalone + marketing/device callers store tokens) ---

  async function login({ email, password, subdomain, captchaToken } = {}) {
    const res = await request({
      method: 'POST',
      path: endpoints.tenantLogin,
      // The device this sign-in is made on (DRG-001): a device-bound session, which store opening requires.
      body: { email, password, subdomain, captchaToken, ...deviceTokenField() },
      auth: 'none',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Email or password is incorrect');
    return extractData(res.body);
  }

  // CAPTCHA-POLICY-001 (SCALE-ACTIVATE-001 S3-21, 2026-09-08): pos-api's `/verify-credentials`
  // verifies `captchaToken` fail-closed through the canonical Turnstile gate, and this op is the
  // wire for two of its three pos-web clients (DeviceUnregistrationPage's admin step, AddPinModal's
  // password step). The body is a REBUILD — a field not named here is dropped — so the token rides
  // explicitly, exactly as `login` above carries it.
  async function verifyCredentials({ email, password, companyId, captchaToken } = {}) {
    const res = await request({
      method: 'POST',
      path: endpoints.verifyCredentials,
      body: { email, password, companyId, captchaToken },
      auth: 'none',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Email or password is incorrect');
    return extractData(res.body);
  }

  async function validateCompanyCode(code) {
    const res = await request({
      method: 'POST',
      path: endpoints.companyValidate,
      body: { code },
      auth: 'none',
    });
    if (res.networkError) {
      throw networkError('Unable to validate company. Please check your connection and try again.');
    }
    if (!res.ok) throw authError(res, 'Failed to validate company code');
    return extractData(res.body);
  }

  // --- Context login (createAuthContext: store tokens, return {user, locations}) ---

  async function loginForContext(email, password, subdomain, captchaToken) {
    const res = await request({
      method: 'POST',
      path: endpoints.tenantLogin,
      // The device this sign-in is made on (DRG-001): a device-bound session, which store opening requires.
      body: { email, password, subdomain, captchaToken, ...deviceTokenField() },
      auth: 'none',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) {
      // Owner ruling Q-ARG-1 ("Required again"): a person whose MFA an administrator's reset cleared is refused a
      // session and handed an enrolment token — a STATE the context raises, not an error. SECREQ S6: so is a person the
      // company MFA policy holds for a passkey or a PIN (`requiresFactorEnrollment`, naming the factor).
      const enrollment = readEnrollmentRequired(res);
      if (enrollment) return enrollment;
      throw authError(res, 'Login failed. Please try again.');
    }
    const rd = extractData(res.body);
    // 🔴 BEFORE the MFA branch, and for two independent reasons.
    //   1. `useLoginForm` orders its own two checks the same way, because a challenge has already
    //      been created and pushed — the phone is buzzing as this resolves, so offering the TOTP
    //      field first asks the user to ignore what their device is already asking them.
    //   2. Without it the PUSH-ONLY shape (no `requiresMfa`) fell through to `persist(rd)` with
    //      `rd.accessToken === undefined` — writing an undefined access token over the token store
    //      on every push-2FA login, and returning `{user: undefined}` to the context.
    if (rd?.requiresAuthenticator) return authenticatorChallenge(rd);
    if (rd?.requiresMfa) return mfaChallenge(rd);
    persist(rd);
    return { user: rd.user, locations: rd.locations };
  }

  /**
   * Install the session an APPROVED push-2FA challenge already minted.
   *
   * `GET /v1/auth/authenticator/status/:challengeId` consumes the challenge and answers with the
   * same canonical envelope `POST /login` would have — access token, refresh token,
   * `buildAuthUserResponse` user, locations — so there is no HTTP leg left to run here. What is
   * left is the part every app would otherwise re-derive: writing that envelope to the token store
   * through the SAME `persist()` login and `verifyMfaForContext` use, so a push sign-in cannot end
   * up with a subtly different stored session than a password one.
   *
   * Synchronous because it performs no I/O: the web token store writes synchronously. Its caller,
   * `@puffin-core/auth-context`'s `completeAuthenticatorChallenge`, AWAITS whatever this returns
   * (SWALLOWED-CATCH-BURNDOWN-001), so a service whose store IS asynchronous (palm-native) may
   * return a promise and have its failure reach the screen.
   *
   * @param {Object} approval - the approved poll's unwrapped `data`
   * @returns {{user: Object, locations: Array}} the shape `createAuthContext` publishes
   * @throws {Error} when the approval carried no access token
   */
  function completeAuthenticatorChallenge(approval) {
    // Not defensive ceremony: this is a WIRE contract with a public, rate-limited endpoint whose
    // 200 can also mean "approved but token issuance failed". Persisting an envelope with no
    // access token would clear the store and leave a signed-in-looking context over no session.
    if (!approval || !approval.accessToken) {
      throw new Error('Sign-in could not be completed. Please sign in again.');
    }
    persist(approval);
    return { user: approval.user, locations: approval.locations };
  }

  async function exchangeCrossAppToken(token) {
    const res = await request({
      method: 'POST',
      path: endpoints.crossAppExchange,
      body: { token },
      auth: 'none',
    });
    if (res.networkError) throw networkError('Cross-app authentication failed');
    if (!res.ok) throw authError(res, 'Cross-app token exchange failed');
    const rd = extractData(res.body);
    if (!rd || !rd.accessToken) {
      throw new Error('Cross-app token exchange returned an invalid response');
    }
    persist(rd);
    return { user: rd.user, locations: rd.locations, returnPath: rd.returnPath };
  }

  /**
   * Resume a session a REDIRECT established (enterprise SSO — Q-UMB-9 unit 3c).
   *
   * A 302 has no body, so an SSO sign-in hands the browser only the httpOnly refresh cookie plus
   * the CSRF token bound to it (in the URL fragment). This stores that token, refreshes through
   * the ONE shared transport to obtain an access token, then reads the canonical user and
   * authorised locations from tenant validate — no second mint path, no token in a URL.
   *
   * @param {string} csrfToken - the CSRF token the redirect carried
   * @returns {Promise<{user: Object, locations: Array}>}
   */
  async function resumeSession(csrfToken) {
    if (typeof csrfToken !== 'string' || csrfToken.length === 0) {
      throw new Error('resumeSession requires the CSRF token the sign-in redirect carried');
    }
    tokenStore.setAuth({ csrfToken });
    await refreshToken();
    const res = await request({ method: 'GET', path: endpoints.tenantValidate, auth: 'storage' });
    if (res.networkError) throw networkError('Unable to resume the session');
    if (!res.ok) throw authError(res, 'Failed to resume the session');
    const rd = extractData(res.body);
    if (!rd || !rd.user) {
      throw new Error('Session resume returned an invalid response');
    }
    tokenStore.setAuth({ user: rd.user, locations: rd.locations });
    return { user: rd.user, locations: rd.locations };
  }

  // --- Device PIN login (raw {token,user}; preserves error.response) ---

  async function loginWithPIN({
    pin,
    deviceFingerprint,
    rememberDevice,
    companyId,
    captchaToken,
  } = {}) {
    const res = await request({
      method: 'POST',
      path: endpoints.loginWithPin,
      body: { pin, deviceFingerprint, rememberDevice, companyId, captchaToken },
      auth: 'none',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    // AUTH-LOGIN-FIDELITY-001: canonical status-carrying error. `err.response` is
    // now the axios-style `{ status, data }` object (was a JSON string) — the PIN
    // consumer (`usePINLogin.parseAuthError`) reads `err.response.data.error.code`.
    if (!res.ok) throw authError(res, 'Login failed');
    return res.body;
  }

  async function requestPasswordReset(email, subdomain) {
    const res = await request({
      method: 'POST',
      path: endpoints.requestPasswordReset,
      body: { email, subdomain },
      auth: 'none',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Failed to request password reset');
    return extractData(res.body);
  }

  // --- Logout (always clears local) ---
  //
  // Bearer-authenticated, EMPTY body: the server revokes the session family from the httpOnly
  // refresh cookie, which the auth-mount-wide cookie path now reaches. There is deliberately no
  // pre-auth `logout` op any more — its one consumer (pos-web) posted `auth: 'none'` to a route
  // that requires a Bearer, so it answered 401 and never revoked anything.

  //
  // No catch (SWALLOWED-CATCH-BURNDOWN-001 U3): the transport never rejects — a failed request
  // RESOLVES `{ok: false}` (transports.js; only a caller's AbortError propagates, and logout passes
  // no signal) — so the old catch guarded nothing but a defect, which now surfaces; `finally` still
  // clears this browser's session first. The revoke's answer is deliberately not read: `clearAuth`
  // removes the CSRF token the refresh cookie is bound to, so even an unrevoked cookie can no longer
  // refresh from this browser; the server-side family expires on its own TTL.

  async function logoutTenant() {
    try {
      const token = tokenStore.getAccessToken();
      if (token) {
        await request({ method: 'POST', path: endpoints.tenantLogout, body: {}, auth: 'storage' });
      }
    } finally {
      tokenStore.clearAuth();
    }
  }

  // --- Validation (network: GET /tenant/validate) ---

  // POS-OFFLINE-STATION-LOCK-001 U15b (owner ruling Q6, "Keep session, all apps"): three answers, never two. A
  // server that could not be REACHED — no answer, a 5xx or 408 — refused nothing, so the session is not over: the
  // check rejects with `SessionUnreachableError` and the boot keeps the person signed in. Only a refusal the server
  // served answers false.
  async function validateTenantToken() {
    if (!tokenStore.getAccessToken()) return false;
    const res = await request({ method: 'GET', path: endpoints.tenantValidate, auth: 'storage' });
    throwIfUnreachable(res, endpoints.tenantValidate);
    if (res.ok !== true) return false;
    // LOC-AUTHZ-001 Phase 3: /validate now returns the canonical, role-aware
    // `locations` set. Refresh the persisted copy so a cold bootstrap hydrates
    // the picker with the CURRENT authorized set (catches a mid-session
    // server-side assignment change) rather than a stale login-time snapshot.
    // Persist ONLY a non-empty set. A store read that fails no longer answers
    // `[]` (the ONE `sessionLocations` rule refuses it 503, which
    // `throwIfUnreachable` above reads as unreachable), so `[]` here is a real
    // answer — but a revocation to zero stays login's and refresh's to apply,
    // never a background validate's.
    const rd = extractData(res.body);
    if (Array.isArray(rd?.locations) && rd.locations.length > 0) {
      tokenStore.setAuth({ locations: rd.locations });
    }
    return true;
  }

  // --- Local getters + refresh ---

  // U15b: the stored person while a token is HELD, expired or not. An expired token used to read as signed out, with
  // no refresh tried — offline, or after a lunch break. The boot's validation decides: its 401 refreshes through this
  // client's transport, and a server that cannot be reached refuses nothing.
  function getCurrentUser() {
    return tokenStore.getAccessToken() ? tokenStore.getUser() : null;
  }

  function getLocations() {
    return tokenStore.getLocations();
  }

  function getToken() {
    return tokenStore.getAccessToken();
  }

  function clearAuthData() {
    tokenStore.clearAuth();
  }

  async function refreshToken() {
    const newAccessToken = await tokenStore.refreshAccessToken(refreshEndpoint);
    if (!newAccessToken) {
      throw new Error('Token refresh failed');
    }
    return { token: newAccessToken };
  }

  return {
    login,
    verifyCredentials,
    validateCompanyCode,
    loginForContext,
    beginMfaEnrollmentForContext: enrollment.begin,
    completeMfaEnrollmentForContext: enrollment.complete,
    beginPasskeyEnrollmentForContext: passkeyEnrollment.begin,
    completePasskeyEnrollmentForContext: passkeyEnrollment.complete,
    completePinEnrollmentForContext: pinEnrollment.complete,
    completeAuthenticatorChallenge,
    exchangeCrossAppToken,
    resumeSession,
    loginWithPIN,
    requestPasswordReset,
    logoutTenant,
    validateTenantToken,
    getCurrentUser,
    getLocations,
    getToken,
    clearAuthData,
    refreshToken,
  };
}
